import { Box, Text, ThemeContext } from '@vouched.id/vault';
import React, { useContext, useMemo } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Tooltips } from '../../tooltips';
import { getAmlCountries, getAmlMatchedTypes, getAmlMatchlists, getAmlPoliticalPositions, itemHasErrorOrWarning } from '../../utils';
import { Dropdown } from '../Dropdown';
import { SingleSpanTable } from '../SingleSpanTable';
import { addIconsToDropdownItems, DropdownItemTypes, DropdownStatusIcon } from '../utils';
import { AMLCaption } from './AMLCaption';
import { AMLDetailsTables } from './AMLDetailsTables';
import { AmlLinkIcon, AmlLinkParentStyles, AmlLinkStyles } from 'components/account/jobs-v2/dropdowns/AML/AmlLinkIcon';
import { humanDate } from 'utils/ui.utils';
export const AMLCompletedResult = ({ amlDetailsData, isAMLOnly, jobErrors, jobStatus, hasMonitoringData }) => {
    const { id: jobSid } = useParams();
    const isAdminRoute = useLocation().pathname.includes('/admin');
    const { colors } = useContext(ThemeContext).theme.tokens;
    const name = amlDetailsData.submittedTerm ?? amlDetailsData.submitted_term;
    const hits = amlDetailsData.totalHits;
    const matchedTypes = getAmlMatchedTypes(amlDetailsData);
    const countries = getAmlCountries(amlDetailsData);
    const yearOfBirth = amlDetailsData.dob ?? 'None';
    const aka = getAmlMatchlists(amlDetailsData);
    const politicalPositions = getAmlPoliticalPositions(amlDetailsData);
    const matches = amlDetailsData.hits?.matches;
    const sanctions = useMemo(() => {
        const sanctionsArray = [];
        if (matches) {
            for (const details of Object.values(matches)) {
                for (const detail of details) {
                    sanctionsArray.push({
                        dates: {
                            start: detail.listing_started_utc
                                ? humanDate(new Date(detail.listing_started_utc), true)
                                : 'N/A',
                            end: detail.listing_ended_utc
                                ? humanDate(new Date(detail.listing_ended_utc), true)
                                : 'N/A'
                        },
                        name: detail.name,
                        url: detail.url,
                        type: detail.type
                    });
                }
            }
        }
        return sanctionsArray;
    }, [matches, humanDate]);
    const { dropdownHasWarnings, dropdownHasRejections } = useMemo(() => {
        const dropdownItemsWithoutIcons = [
            {
                text: 'AML',
                type: DropdownItemTypes.ConfidenceDropdownItem,
                available: true,
                hasErrorOrWarning: {
                    /*
                     * AMLError will be present on new IDV+AML jobs and AML-only jobs when there is one or more hits.
                     * AMLError is present on old IDV+AML jobs with non PEP hits. We are choosing to show them as is.
                     * AMLError is never present on old AML-only jobs, and we are choosing to show them as rejected if they have any hits.
                     */
                    hasError: itemHasErrorOrWarning(jobErrors, 'AMLError').hasError ||
                        (isAMLOnly && hits > 0),
                    hasWarning: false
                }
            }
        ];
        return addIconsToDropdownItems(dropdownItemsWithoutIcons);
    }, [jobErrors, itemHasErrorOrWarning, isAMLOnly, hits]);
    return (React.createElement(Dropdown, { statusIcon: React.createElement(DropdownStatusIcon, { dropdownHasRejections: dropdownHasRejections, dropdownHasWarnings: dropdownHasWarnings, parentJobStatus: jobStatus }), title: "AML" },
        React.createElement(Box, null,
            React.createElement(AMLCaption, null),
            hasMonitoringData && (React.createElement(Box, { marginBottom: "spacing.large", paddingX: "spacing.large", paddingY: "spacing.default", style: AmlLinkParentStyles, width: "fit-content" },
                React.createElement(NavLink, { style: AmlLinkStyles, to: `${isAdminRoute ? '/admin' : '/account'}/jobs/${jobSid}/monitoring` },
                    React.createElement(Text, null, "View Monitoring results "),
                    React.createElement(AmlLinkIcon, null)))),
            React.createElement(SingleSpanTable, { items: [
                    {
                        label: 'Name',
                        value: name,
                        labelTooltip: Tooltips.NameAML
                    },
                    {
                        label: 'Hits',
                        value: hits,
                        ...(hits && { valueColor: colors.red }),
                        labelTooltip: Tooltips.HitsAML
                    },
                    {
                        label: 'Matched Types',
                        value: matchedTypes,
                        ...(matchedTypes !== 'None' && { valueColor: colors.red }),
                        labelTooltip: Tooltips.MatchedTypesAML
                    },
                    {
                        label: 'Countries',
                        value: countries,
                        labelTooltip: Tooltips.CountriesAML
                    },
                    {
                        label: 'Year of Birth',
                        value: yearOfBirth,
                        labelTooltip: Tooltips.YearOfBirthAML
                    },
                    {
                        label: 'AKA',
                        value: aka,
                        labelTooltip: Tooltips.AkaAML
                    },
                    {
                        label: 'Political Positions',
                        value: politicalPositions,
                        labelTooltip: Tooltips.PoliticalPositionsAML
                    }
                ], numColumns: 2 }),
            sanctions.length > 0 ? (React.createElement(React.Fragment, null,
                React.createElement(Text, null, "The following table contains details on the Matched Types indicated above."),
                React.createElement(AMLDetailsTables, { sanctions: sanctions }))) : null)));
};
